import { Model } from "../../models/Model";
import * as THREE from "three";
import URDFLoader, { URDFRobot } from "urdf-loader";
import { EventEmitter } from "events";
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { ColladaLoader } from "three/examples/jsm/loaders/ColladaLoader";
import { GLTFResult } from "../../api/threed";
import { downloadOrgBlob, uploadOrgBlob } from "./UploadBlob";
import { downloadBlob, loadOrgModel, uploadBlob } from "../../api/connection";
import JSZip, { file } from "jszip";
import { OrgBlobType, createOrgBlob } from "../../models/OrgBlob";

export enum MLEvents {
  ModelLoaded = "modelLoaded",
}

export async function loadGLTFFromBlob(blobId: string): Promise<THREE.Object3D> {
  const arrayBuffer = await downloadOrgBlob(blobId);
  const blob = new Blob([arrayBuffer as ArrayBuffer], { type: 'model/gltf-binary' });
  const gltfLoader = new GLTFLoader();
  const objectUrl = URL.createObjectURL(blob);
  const gltf: GLTFResult = await new Promise((resolve, reject) => {
    gltfLoader.load(objectUrl, resolve, undefined, reject);
  });
  const model = gltf.scene; //.children[0] as THREE.Object3D;
  model.userData.gltf = gltf; // Store the GLTF result in userData for later use
  return model;
}

export async function loadDaeFromBlob(blobId: string): Promise<THREE.Object3D> {
  const arrayBuffer = await downloadOrgBlob(blobId);
  const blob = new Blob([arrayBuffer as ArrayBuffer], { type: 'model/vnd.collada+xml' });
  const loader = new ColladaLoader();
  const objectUrl = URL.createObjectURL(blob);
  const collada: any = await new Promise((resolve, reject) => {
    loader.load(objectUrl, resolve, undefined, reject);
  });
  const model = collada.scene;
  return model;
}

export class ModelLoader extends EventEmitter {
  public modelObj?: THREE.Object3D;
  public robot?: URDFRobot;
  private containerObj: THREE.Group;
  public model!: Model;

  constructor() {
    super();
    this.containerObj = new THREE.Group();
    this.modelObj = new THREE.Mesh(
      new THREE.BoxGeometry(1, 1, 1),
      new THREE.MeshBasicMaterial({ color: 0xffffff })
    );
    this.containerObj.add(this.modelObj);
  }

  public getModelName(): string {
    return this.model ? this.model.name : "Model not loaded";
  }

  public emitEvent(event: MLEvents, ...args: any[]) {
    this.emit(event, ...args);
  }

  public onEvent(event: MLEvents, listener: (...args: any[]) => void) {
    this.on(event, listener);
  }

  public getContainer(): THREE.Group {
    return this.containerObj;
  }

  public async loadModel(model: Model) {
    this.model = model;
    this.containerObj.clear();
    this.modelObj = undefined; // Reset the model object
    this.robot = undefined; // Reset the robot object
    // debugger;
    if (!model.source.url && !model.source.blobId) {
      this.modelObj = new THREE.Mesh(
        new THREE.BoxGeometry(1, 1, 1),
        new THREE.MeshBasicMaterial({ color: 0xff0000 })
      );
    } else if (model.source.blobId) {
      const blobData = await loadOrgModel(createOrgBlob(model.source.blobId));
      if (blobData.type === OrgBlobType.URDF) {
        this.modelObj = await this.loadURDFModelFromBlobStorage(model);
      } else if (blobData.type === OrgBlobType.DAE) {
        this.modelObj = await loadDaeFromBlob(model.source.blobId);
      } else {
        this.modelObj = await loadGLTFFromBlob(model.source.blobId);
      }
    } else if (model.source.url?.endsWith('.urdf')) {
      this.modelObj = await this.loadURDFModel(model);
    } else {
      this.modelObj = new THREE.Mesh(
        new THREE.BoxGeometry(1, 1, 1),
        new THREE.MeshBasicMaterial({ color: 0x0000ff })
      );
    }
    this.adapt();
    this.containerObj.clear();
    this.containerObj.add(this.modelObj);
    // this.containerObj
    // this.modelObj.renderOrder = 999; // Ensure the model is rendered after other objects
    // this.modelObj.traverse((child) => {
    //   if (child instanceof THREE.Mesh) {
    //     // child.material.transparent = false; // Disable transparency
    //     // child.material.opacity = 1.0; // Ensure full opacity
    //     // child.material.alphaTest = 0.5; // Adjust if using alpha mask
    //     // child.material.needsUpdate = true; // Update material
    //   }
    // });

    this.emitEvent(MLEvents.ModelLoaded, this.modelObj);
    return this.containerObj;
  }

  public adapt(): void {
    const a = this.model.adaptation;
    this.modelObj!.scale.set(a.scale, a.scale, a.scale);
    this.modelObj!.rotation.set(a.rotation.x, a.rotation.y, a.rotation.z);
    this.modelObj!.position.set(a.position.x, a.position.y, a.position.z);
  }

  public async loadURDFModel(model: Model): Promise<THREE.Object3D> {
    return new Promise((resolve, reject) => {
      if (model.source.url) {
        const loader = new URDFLoader();
        const packageRoot = model.source.packageRoot || model.source.url.substring(0, model.source.url.lastIndexOf('/') - 4); // Remove 'urdf/' from the end
        const packageName = packageRoot.endsWith('/') ? packageRoot.split('/').slice(-2)[0] : packageRoot.split('/').slice(-1)[0];
        const packageLink = packageRoot.endsWith('/') ? packageRoot : packageRoot + '/';
        loader.packages = {
          [packageName]: packageLink,
        };
        loader.load(
          model.source.url,
          (urdf) => {
            this.robot = urdf;
            // this.robot.visual.obj.traverse((child) => {
            //   if (child instanceof THREE.Mesh) {
            //     child.material = new THREE.MeshStandardMaterial({
            //       color: 0xff0000,
            //       metalness: 0.5,
            //       roughness: 0.5,
            //       side: THREE.DoubleSide
            //     });
            //     // child.receiveShadow = true; // Enable shadow receiving for meshes
            //   }
            // });
            resolve(urdf);
          },
          undefined,
          (error) => {
            console.error("Error loading URDF model:", error);
            reject(error);
          }
        );
      } else {
        reject(new Error("Model source URL is not defined"));
      }
    });
  }

  public async loadURDFModelFromBlobStorage(model: Model): Promise<THREE.Object3D> {
    const blobData = await downloadOrgBlob(model.source.blobId!);
    const zip = new JSZip();
    const zipContent = await zip.loadAsync(blobData);
    const files = new Map<string, Blob>();
    // get files out of zip
    // for every file, create a blob url and index it by its name in a map
    let urdfName = '';
    for (const [path, file] of Object.entries(zipContent.files)) {
      if (!file.dir) {
        const data = await file.async(file.name.endsWith('.urdf') ? 'string' : 'arraybuffer')
        const blob = new Blob([data]);
        if (path.endsWith('.urdf')) {
          urdfName = path;
        }
        files.set(path, blob);
      }
    }

    if (files.size === 0 || !urdfName) {
      throw new Error("No files found in URDF ZIP");
    }
    const loader = new URDFLoader();
    const manager = new THREE.LoadingManager();
    manager.setURLModifier((url) => {
      let filePath = url;

      // Remove any leading slashes for consistency with ZIP paths
      filePath = filePath.replace('../', ''); // Handle relative paths
      filePath = filePath.replace(/^\/+/, '');
      const fileContent = files.get(filePath);
      if (!fileContent) {
        console.warn(`File not found in ZIP: ${filePath}`);
        return '';
      }
      return URL.createObjectURL(new Blob([fileContent]));
    })
    loader.manager = manager;

    // const urdfFileName = model.source.url.split('/').pop();

    const urdfContent = files.get(urdfName);
    const urdfString = await urdfContent!.text();
    const urdf = loader.parse(urdfString);
    console.log('URDF loaded from Blob:', urdf);
    this.robot = urdf;
    return urdf;
  }

  public async packageUrdfToBlob(model: Model) {
    if (!model.source.url) {
      throw new Error("Model source URL is not defined");
    }

    const urdfResponse = await fetch(model.source.url);
    if (!urdfResponse.ok) {
      throw new Error(`Failed to fetch URDF: ${urdfResponse.status} ${urdfResponse.statusText}`);
    }
    const urdfText = await urdfResponse.text();
    console.log("Fetched URDF text:", urdfText);

    const parser = new DOMParser();
    const urdfXml = parser.parseFromString(urdfText, "text/xml");
    const meshElements = urdfXml.getElementsByTagName("mesh");
    const meshUrls = Array.from(meshElements)
      .map((mesh) => mesh.getAttribute("filename"))
      .filter((url): url is string => !!url && !url.startsWith("file://"));
    console.log("Extracted mesh URLs:", meshUrls);

    const files = new Map<string, ArrayBuffer | string>();
    const urdfFileName = `urdf/${model.source.url.split('/').pop() || 'urdf/robot.urdf'}`;
    console.log("URDF file name for ZIP:", urdfFileName);
    files.set(urdfFileName, urdfText);
    const baseUrl = model.source.url.substring(0, model.source.url.lastIndexOf('/') + 1);

    const packageRoot = model.source.packageRoot
      ? model.source.packageRoot.endsWith('/')
        ? model.source.packageRoot
        : `${model.source.packageRoot}/`
      : baseUrl.substring(0, baseUrl.lastIndexOf('/') - 4); // Remove 'urdf/' from the end

    console.log({ baseUrl, packageRoot });

    const packageName = packageRoot.split('/').slice(-2)[0] || 'package';
    for (const meshUrl of meshUrls) {
      let fullUrl = meshUrl;
      let filePath = meshUrl;

      // Handle package:// or relative paths
      if (meshUrl.startsWith('package://')) {
        const relativePath = meshUrl.replace(`package://${packageName}/`, '');
        fullUrl = `${packageRoot}${relativePath}`;
        filePath = `${relativePath}`;
      } else if (meshUrl.startsWith('../')) {
        const relativePath = meshUrl.replace(`../`, '');
        fullUrl = `${packageRoot}${relativePath}`;
        filePath = `${relativePath}`;
      } else if (!meshUrl.startsWith('http')) {
        fullUrl = new URL(meshUrl, baseUrl).href;
        filePath = `${meshUrl.replace(/^\/+/, '')}`; // Ensure mesh files are in meshes/
      }

      try {
        const response = await fetch(fullUrl);
        if (!response.ok) {
          console.warn(`Failed to fetch mesh: ${fullUrl} (${response.status})`);
          continue;
        }
        const contentType = response.headers.get('content-type') || '';
        const isText = contentType.includes('text') || contentType.includes('xml') || fullUrl.endsWith('.dae');
        const content = isText ? await response.text() : await response.arrayBuffer();
        files.set(filePath, content);
      } catch (error) {
        console.warn(`Error fetching mesh: ${fullUrl}`, error);
        continue;
      }
    }

    // Create a ZIP archive
    const zip = new JSZip();
    let modifiedUrdf = urdfText;

    // Modify URDF to use relative paths (e.g., '../meshes/irb5400/visual/link_6.dae')
    for (const meshUrl of meshUrls) {
      const relativePath = meshUrl.startsWith('package://')
        ? `../${meshUrl.replace(`package://${packageName}/`, '')}`
        : `${meshUrl.replace(/^\/+/, '')}`;
      modifiedUrdf = modifiedUrdf.replace(new RegExp(meshUrl, 'g'), relativePath);
    }

    zip.file(urdfFileName, modifiedUrdf);

    // Add mesh files to ZIP
    for (const [path, content] of files) {
      if (path !== urdfFileName) {
        zip.file(path, content);
      }
    }

    // const zipBlob = await zip.generateAsync({ type: 'blob' });
    // const url = URL.createObjectURL(zipBlob);
    // const a = document.createElement('a');
    // a.href = url;
    // a.download = `${model._id}.zip`;
    // document.body.appendChild(a);
    // a.click();
    // document.body.removeChild(a);

    // Generate Blob
    const blob = await zip.generateAsync({ type: 'arraybuffer' });
    const uploadRes = await uploadOrgBlob(OrgBlobType.URDF, blob);
    console.log("Uploaded URDF package to Blob storage:", uploadRes);
    return uploadRes._id;
  }
}
